import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewEncapsulation,
} from "@angular/core";
import { Subject } from "rxjs";
import { FuseConfirmationService } from "@nx-workspace/fuse";
import { CdkDragDrop, CdkDragEnter, CdkDragExit, moveItemInArray } from "@angular/cdk/drag-drop";
import { BuilderService } from "./builder.service";
import { Field, FieldType, FormDefinition } from "../vforms-model";
import { MatDialog } from "@angular/material/dialog";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { takeUntil } from "rxjs/operators";
import { ActivatedRoute } from "@angular/router";
import { DragStylingService } from "../drag-styling.service";
import { FieldEditorComponent } from "../field-editor/field-editor.component";
import { FormViewModel } from "../view/form-view.model";
import { FormViewDialogComponent } from "../view/dialog/form-view-dialog.component";
import { ConditionalSettingsComponent } from "../expression-settings/conditional-settings.component";

@Component({
    selector: "form-builder",
    templateUrl: "./form-builder.component.html",
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormBuilderComponent implements OnInit, OnDestroy {
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    form: FormGroup;

    formDefinition: FormDefinition;

    fieldTypes: FieldType[];

    callbackUrl: string;

    isDragging: boolean;

    @Input()
    strDefinition: string;

    set definition(form: FormDefinition) {
        this.formDefinition = form;
        this.form.patchValue({
            title: this.formDefinition.title,
            description: this.formDefinition.description,
        });
        if (form.excludedFields?.length > 0) {
            this.fieldTypes = this.fieldTypes.filter((fieldType) => {
                return form.excludedFields?.indexOf(fieldType.id) === -1;
            });
        }

        if (this.formDefinition.requiredDescription) {
            this.form.get("description").setValidators([Validators.required]);
        } else {
            this.form.get("description").clearValidators();
        }
        this.form.get("description").updateValueAndValidity();
        this._changeDetector.detectChanges();
    }

    @Output()
    onCancel = new EventEmitter<boolean>();

    @Output()
    onSave = new EventEmitter<string>();

    constructor(
        private _changeDetector: ChangeDetectorRef,
        private _formBuilder: FormBuilder,
        private _builderService: BuilderService,
        public _dialog: MatDialog,
        private _fuseConfirmationService: FuseConfirmationService,
        private route: ActivatedRoute,
        private dragStylingService: DragStylingService
    ) {
        this.fieldTypes = this._builderService._fieldTypes;
        this.form = this._formBuilder.group({
            title: [null, [Validators.required]],
            description: [null, []],
        });
    }

    ngOnInit(): void {
        this.form.valueChanges.pipe(takeUntil(this._unsubscribeAll)).subscribe((value) => {
            const data = this.form.getRawValue();
            this.formDefinition.title = data.title;
            this.formDefinition.description = data.description;
            this._changeDetector.detectChanges();
        });

        this.route.queryParams.subscribe((params) => {
            if (params.callback) {
                this.callbackUrl = atob(params.callback);
            }
        });

        this.dragStylingService.isDragging$.pipe(takeUntil(this._unsubscribeAll)).subscribe((isDragging) => {
            this.isDragging = isDragging;
        });

        if (!this.formDefinition) {
            this.definition = this._builderService.fromJson(this.strDefinition);
        }
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    get descriptionRequired(): boolean {
        return this.form.get("description").hasValidator(Validators.required);
    }

    drop(event: CdkDragDrop<any[]>) {
        if (event.previousContainer === event.container) {
            moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
        } else {
            const fieldType: FieldType = event.item.data;
            const field = fieldType.createField();
            const dialogRef = this._dialog.open(FieldEditorComponent, {
                data: {
                    field,
                },
            });
            dialogRef.afterClosed().subscribe((result) => {
                if (result) {
                    this.formDefinition.addField(field, event.currentIndex);
                    this._changeDetector.detectChanges();
                }
            });

            if (event.previousContainer.data) {
                this.onSourceListEntered(event);
            }
        }
    }

    edit(field: Field) {
        const dialogRef = this._dialog.open(FieldEditorComponent, {
            data: {
                field,
            },
        });
        dialogRef.afterClosed().subscribe((result) => {
            if (result) {
                this._changeDetector.detectChanges();
            }
        });
    }

    delete(field: Field) {
        const dialogRef = this._fuseConfirmationService.open({
            title: "Excluir campo",
            message: `Você tem certeza que deseja excluir o campo ${field.label}?`,
            icon: {
                show: true,
                name: "heroicons_outline:exclamation",
                color: "warn",
            },
            actions: {
                confirm: {
                    show: true,
                    label: "Sim",
                    color: "warn",
                },
                cancel: {
                    show: true,
                    label: "Não",
                },
            },
            dismissible: true,
        });
        dialogRef.afterClosed().subscribe((result) => {
            if (result === "confirmed") {
                this.formDefinition.delete(field);
                this._changeDetector.detectChanges();
            }
        });
    }

    duplicate(field: Field) {
        this.formDefinition.duplicate(field);
        this._changeDetector.detectChanges();
    }

    render() {
        const model = new FormViewModel();
        model.fields = this._builderService.toFormlyFields(this.formDefinition);
        model.title = this.formDefinition.title;
        model.description = this.formDefinition.description;
        this._dialog.open(FormViewDialogComponent, {
            panelClass: "form-view-dialog",
            data: {
                form: model,
            },
        });
    }

    save() {
        if (!this.form.valid) {
            this.form.markAllAsTouched();
            return;
        }
        const definition = this._builderService.toJson(this.formDefinition);
        this.onSave.emit(definition);
    }

    cancel() {
        this.onCancel.emit(true);
    }

    openFieldExpressionSettings() {
        let dialogRef = this._dialog.open(ConditionalSettingsComponent, {
            data: {
                form: this.formDefinition,
            },
        });

        dialogRef.afterClosed().subscribe();
    }

    onSourceListExited(event: CdkDragExit<any>) {
        const index = this.fieldTypes.indexOf(event.item.data);
        this.fieldTypes.splice(index + 1, 0, { ...event.item.data, temp: true });
    }

    onSourceListEntered(event: CdkDragEnter<any>) {
        const index = this.fieldTypes.findIndex((fieldType: any) => fieldType.temp);
        this.fieldTypes.splice(index, 1);
    }
}
